import { AreaSchema, paginatedResponseSchema } from '@gestion-permisos/contracts';
import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useApi } from '../api/api-context.js';
import { Field, INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON } from '../admin/admin-ui.js';
import { ChoiceList } from '../components/choice-list.js';
import { usePermissionTypes } from './catalog-api.js';
import { type RequestQueryFilters } from './request-list.js';

/** Orden de la lista blanca. Vacío: el orden por defecto de la API. */
const SORT_OPTIONS: readonly [value: string, label: string][] = [
  ['', 'Más recientes'],
  ['permissionDate', 'Fecha del permiso, más antigua'],
  ['-permissionDate', 'Fecha del permiso, más reciente'],
  ['createdAt', 'Creación, más antigua'],
  ['-createdAt', 'Creación, más reciente'],
  ['requestNumber', 'Número'],
  ['-requestNumber', 'Número, descendente'],
];

/** Áreas del selector, solo si la pantalla es de administración (`enabled`). */
function useAdminAreas(enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['requests', 'areas'],
    queryFn: () => api.get('/api/v1/areas?pageSize=100', paginatedResponseSchema(AreaSchema)),
    enabled,
  });
}

const FORM_BOX = 'mt-4 rounded-md border border-slate-200 bg-white p-4';

/**
 * Filtros de H-02. Las fechas se envían tal como se escriben. El área solo se ofrece al
 * administrador. No hay filtro por empleado ni calendario de equipo. `className` sustituye la caja por
 * defecto (borde, fondo y margen), p. ej. cuando el formulario ya va dentro de una tarjeta.
 */
export function RequestSearchForm({
  filters,
  onApply,
  showArea,
  className = FORM_BOX,
}: {
  filters: RequestQueryFilters;
  onApply: (next: RequestQueryFilters) => void;
  showArea: boolean;
  className?: string;
}) {
  const types = usePermissionTypes();
  const areas = useAdminAreas(showArea);
  const [draft, setDraft] = useState<RequestQueryFilters>(filters);
  const set = (key: keyof RequestQueryFilters, value: string) => setDraft((current) => ({ ...current, [key]: value }));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onApply(showArea ? draft : { ...draft, areaId: '' });
  };

  return (
    <form noValidate onSubmit={submit} aria-label="Filtros de solicitudes" className={`space-y-3 ${className}`}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field id="filtro-q" label="Buscar">
          <input id="filtro-q" value={draft.q} onChange={(event) => set('q', event.target.value)} className={INPUT} />
        </Field>
        <Field id="filtro-desde" label="Desde (fecha del permiso)">
          <input id="filtro-desde" value={draft.from} placeholder="2030-03-01" onChange={(event) => set('from', event.target.value)} className={INPUT} />
        </Field>
        <Field id="filtro-hasta" label="Hasta (fecha del permiso)">
          <input id="filtro-hasta" value={draft.to} placeholder="2030-03-31" onChange={(event) => set('to', event.target.value)} className={INPUT} />
        </Field>
        <Field id="filtro-tipo" label="Tipo de permiso">
          <ChoiceList
            id="filtro-tipo"
            value={draft.permissionTypeId}
            options={[{ value: '', label: 'Todos' }, ...(types.data?.items.map((type) => ({ value: type.id, label: type.name })) ?? [])]}
            onChange={(value) => set('permissionTypeId', value)}
            className={INPUT}
          />
        </Field>
        {showArea ? (
          <Field id="filtro-area" label="Área">
            <ChoiceList
              id="filtro-area"
              value={draft.areaId}
              options={[{ value: '', label: 'Todas' }, ...(areas.data?.items.map((area) => ({ value: area.id, label: area.name })) ?? [])]}
              onChange={(value) => set('areaId', value)}
              className={INPUT}
            />
          </Field>
        ) : null}
        <Field id="filtro-orden" label="Orden">
          <ChoiceList
            id="filtro-orden"
            value={draft.sort}
            options={[
              ...SORT_OPTIONS.map(([value, label]) => ({ value, label })),
              ...(SORT_OPTIONS.every(([value]) => value !== draft.sort) ? [{ value: draft.sort, label: draft.sort }] : []),
            ]}
            onChange={(value) => set('sort', value)}
            className={INPUT}
          />
        </Field>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={PRIMARY_BUTTON}>
          Filtrar
        </button>
        <button
          type="button"
          className={SECONDARY_BUTTON}
          onClick={() => onApply({ q: '', from: '', to: '', permissionTypeId: '', areaId: '', sort: '' })}
        >
          Quitar filtros
        </button>
      </div>
    </form>
  );
}
